/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Divider
} from '@chakra-ui/react';

import api from "utils/api";

const PlakaDegistirModal = ({ 
  isOpen, 
  onClose, 
  aracData,
  onPlakaGecmisiOpen,
  onSuccess  // ✅ Plaka değiştikten sonra parent'ı güncellemek için
}) => {
  const [yeniPlaka, setYeniPlaka] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  // Modal açıldığında sıfırla
  useEffect(() => {
    if (isOpen) {
      setYeniPlaka('');
      setError('');
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleShowPlakaGecmisi = () => {
    if (onPlakaGecmisiOpen) {
      onPlakaGecmisiOpen();
    }
  };

  const handlePlakaChange = async () => {
    if (!yeniPlaka.trim()) {
      setError('Lütfen yeni plakayı giriniz.');
      return;
    }

    if (!aracData?.imei) {
      setError('Araç IMEI bilgisi bulunamadı.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.put(`/api/devices/by-imei/${aracData.imei}/change-plate`, {
        yeni_plaka: yeniPlaka.trim().toUpperCase(),
        eski_plaka: aracData.plaka || '',
        imei: aracData.imei,
        traccar_device_id: aracData.id || null,
        change_reason: 'Admin panelinden değiştirildi'
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Hata mesajını düzgün parse et
        let errorMessage = 'Plaka değiştirme işlemi başarısız oldu.';
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Validation hataları array olarak gelebilir
            errorMessage = errorData.detail.map(err => err.msg || err.loc?.join('.') + ': ' + err.msg).join(', ');
          } else if (errorData.detail.msg) {
            errorMessage = errorData.detail.msg;
          }
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      toast({
        title: 'Başarılı!',
        description: result.message || 'Plaka başarıyla değiştirildi.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Modal'ı kapat
      onClose();
      
      // Parent component'ı güncelle (sayfa yenileme yerine)
      if (onSuccess) {
        onSuccess();
      }

    } catch (err) {
      // Hata mesajını düzgün parse et
      let errorMessage = 'Plaka değiştirme işlemi sırasında bir hata oluştu.';
      if (err.message) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err.toString && err.toString() !== '[object Object]') {
        errorMessage = err.toString();
      }
      
      setError(errorMessage);
      
      toast({
        title: 'Hata!',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!aracData) return null;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose}
      closeOnOverlayClick={!loading}
      closeOnEsc={!loading}
      size="md"
      isCentered
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" maxW="520px" bg="white" p={1}>
        <ModalHeader textAlign="center" py={2}>
          <VStack spacing={1}>
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              Plaka Değiştir
            </Text>
            <Text fontSize="xs" color="gray.400">
              {aracData.name || aracData.imei || 'Araç'} için yeni plaka girin
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton size="sm" />
        <ModalBody px={6} py={4}>
          <VStack spacing={4} align="stretch">
            {/* Mevcut bilgiler */}
            <Box p={4} borderWidth="1px" borderRadius="lg" bg="gray.50">
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Firma Adı:</Text>
                  <Text>{aracData.firma_ismi || "Belirsiz"}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">IMEI:</Text>
                  <Text>{aracData.imei}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Mevcut Plaka:</Text>
                  <Text fontWeight="bold" color="blue.500">
                    {aracData.plaka || "Belirsiz"}
                  </Text>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* Yeni plaka girişi */}
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Yeni Plaka</FormLabel>
              <Input
                variant="flushed"
                size="sm"
                value={yeniPlaka}
                onChange={(e) => setYeniPlaka(e.target.value.toLocaleUpperCase("tr-TR"))}
                placeholder="Örn: 34ABC1234"
                isDisabled={loading}
                maxLength={30}
              />
            </FormControl>

            {/* Hata mesajı */}
            {error && (
              <Alert status="error">
                <AlertIcon />
                <AlertTitle>Hata!</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter px={6} py={4}>
          <HStack spacing={3}>
            <Button 
              variant="ghost" 
              onClick={handleClose}
              isDisabled={loading}
              size="sm"
            >
              İptal
            </Button>
            
            <Button
              colorScheme="blue"
              onClick={handlePlakaChange}
              isLoading={loading}
              loadingText="Değiştiriliyor..."
              isDisabled={!yeniPlaka.trim() || loading}
              size="sm"
            >
              Plakayı Değiştir
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PlakaDegistirModal;
